import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import type { FilterDefinition } from "@/shared/components/filters/FilterDefinition";
import { useCustomFieldValueSuggestions } from "@/shared/components/filters/useSuggestionSources";
import type { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import { buildCustomFieldFilterDefinitions } from "./customFieldGridBuilders";

/**
 * Filter definitions for the current org's Ready custom fields on
 * `entityType`, with value suggestions on string fields. Empty while the
 * release-custom-fields flag is off or the fields are still loading. Use it
 * on a page that filters an entity without rendering that entity's grid; a
 * grid gets the same definitions from `useCustomFieldGridIntegration`.
 */
export function useCustomFieldFilterDefinitions(
  entityType: TargetEntityType,
): FilterDefinition[] {
  const flagEnabled = useCheckFeatureFlag("release-custom-fields");
  const orgId = useCurrentOrgId();
  const readyQuery = useReadyCustomFields({
    entityType,
    orgId,
    enabled: flagEnabled,
  });
  const valueSuggestions = useCustomFieldValueSuggestions();
  return React.useMemo(
    () =>
      buildCustomFieldFilterDefinitions(
        flagEnabled ? readyQuery.data ?? [] : [],
        "",
        valueSuggestions,
      ),
    [flagEnabled, readyQuery.data, valueSuggestions],
  );
}
